import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Nav, NavItem, NavLink, TabContent as ReactstrapTabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import dayjs from 'dayjs';
import PatientCareOrderHistory from './comps/patientCareOrderHistory';
import PatientHistorySummary from './comps/patientHistorySummary';
import { onPatientDetailsViewMount } from 'actions/rd2RefactorActions';
import useLoadPatientDetails from './hooks/useLoadPatientDetails';
import useLoadPatientCareOrderHistory from './hooks/useLoadPatientCareOrderHistory';
import PatientAppointmentHistory from './comps/patientAppointmentHistory';
import PatientUpcomingAppointments from './comps/patientUpcomingAppointments';
import useLoadPatientAppointmentHistory from './hooks/useLoadPatientAppointmentHistory';
import useLoadPatientCoverage from './hooks/useLoadPatientCoverage';
import ActiveErrors from 'components/common/activeErrors';
import LoadingIndicator from 'components/loadingIndicator';

import './css/patientDetailsView.css';
import useLoadPatientOpenWorkflows from './hooks/useLoadPatientOpenWorkflows';
import PatientOpenWorkflows from './comps/patientOpenWorkflows';
import PatientDetailsTabbedCollapsable from './comps/patientDetailsTabbedCollapsable';
import { useAppSelector } from '@/store';
import withPermission from '@/components/common/withPermission';
import withConfigEnabler from '@/components/common/withConfigOnOff';
import Mhd2Permissions from '@/constants/permissions';
import { selectUserPermissions } from '@/reducers/authReducer';
import { ActiveTabProvider, useActiveTab } from './comps/activeTabContext';
import { compose } from 'redux';

export enum PatientInfoTabName {
    patientDetails = "patientDetails",
    upcomingAppointment = "upcomingAppointment",
    appointmentHistory = "appointmentHistory",
    careOrderHistory = "careOrderHistory",
    decisionSupportWorkflowHistory = "decisionSupportWorkflowHistory",
    patientCareOrderDetails = "patientCareOrderDetails",
    coverage = "coverage",
    empty = "",
}

export default function PatientDetailsView(props: any) {
    const dispatch = useDispatch();

    const { referenceId = '' } = useParams<{ referenceId: string }>();

    const userPermissions = useAppSelector(selectUserPermissions);
    const patient = useSelector((state: any) => state.activePatient);
    const patientConfig = useSelector((state: any) => state.config.patient);
    const schedulingConfig = useSelector((state: any) => state.config.scheduling)
    const isPatientOpenWorkflowsLoading = useSelector((state: any) => state.activePatient.isLoadingOpenWorkflows);

    const [isPatientLoading, setIsPatientLoading] = useState(false);
    const [isPatientCoverageLoading, setIsPatientCoverageLoading] = useState(false);
    const [isPatientAppointmentHistoryLoading, setIsPatientAppointmentHistoryLoading] = useState(false);
    const [isPatientCareOrderHistoryLoading, setIsPatientCareOrderHistoryLoading] = useState(false);

    const initialTab = patientConfig.isPatientHistoryEnabled ? PatientInfoTabName.upcomingAppointment :
        (patientConfig.isPatientCareOrderHistoryEnabled ? PatientInfoTabName.careOrderHistory :
            (PatientInfoTabName.decisionSupportWorkflowHistory));

    useEffect(() => {
        dispatch(onPatientDetailsViewMount());
    }, [dispatch])

    if (!referenceId) {
        throw new Error("Missing Patient Id");
    }

    const isPatientAlreadyActive = (patient?.id === referenceId);
    const isPatientAlreadyLoaded = isPatientAlreadyActive && (patient.details.firstName !== undefined);
    const shouldLoadPatient = !isPatientAlreadyLoaded && userPermissions?.includes(Mhd2Permissions.patientView);
    useLoadPatientDetails(
        referenceId,
        shouldLoadPatient,
        setIsPatientLoading
    );

    const shouldLoadPatientCoverage = patient.id
        && patientConfig.enablePatientAccessCuratorInsuranceCoverage
        && patientConfig.enablePatientAccessCurator
        && isPatientAlreadyLoaded
        && userPermissions?.includes(Mhd2Permissions.patientView);
    useLoadPatientCoverage(
        patient.details,
        patientConfig.patientAccessCuratorDataValidityInDays,
        patientConfig.enablePatientAccessCuratorCoverageAutoFetch,
        shouldLoadPatientCoverage,
        setIsPatientCoverageLoading
    );

    const shouldLoadPatientAppointmentHistory = patient.id
        && patientConfig.isPatientHistoryEnabled
        && userPermissions?.includes(Mhd2Permissions.appointmentView);
    useLoadPatientAppointmentHistory(
        patient.id,
        shouldLoadPatientAppointmentHistory,
        setIsPatientAppointmentHistoryLoading
    );

    const shouldLoadPatientCareOrderHistory = patient.id
        && patientConfig.isPatientCareOrderHistoryEnabled
        && userPermissions?.includes(Mhd2Permissions.orderView);
    useLoadPatientCareOrderHistory(
        patient.id,
        shouldLoadPatientCareOrderHistory,
        setIsPatientCareOrderHistoryLoading
    );

    const shouldLoadPatientOpenWorkflows = patient.id
        && patientConfig.isDecisionSupportWorkflowHistoryEnabled
        && userPermissions?.includes(Mhd2Permissions.appointmentView);
    useLoadPatientOpenWorkflows(
        patient.id,
        shouldLoadPatientOpenWorkflows,
        schedulingConfig.bookOpenDecisionSupportWorkflowMaxAgeInDays,
    );

    const careOrders = useSelector((state: any) => state.activePatient.careOrderHistory);
    const openCareOrders = careOrders?.filter((careOrder: any) =>
        (careOrder.statusCode !== 'C')
        && (!careOrder.expirationDate || dayjs(careOrder.expirationDate) >= dayjs())
    );

    const openCareOrdersCount = openCareOrders?.length;

    const hidePatientHistorySummary = () => {
        return !patientConfig.isPatientHistoryEnabled && !patientConfig.isPatientCareOrderHistoryEnabled && !patientConfig.isDecisionSupportWorkflowHistoryEnabled
    }

    return (
        <div>
            <ActiveErrors />
            {(isPatientLoading || isPatientCoverageLoading) &&
                <LoadingIndicator />
            }
            {!isPatientLoading && !isPatientCoverageLoading &&
                <>
                    <PatientDetailsTabbedCollapsable />
                    <br />
                    {(isPatientAppointmentHistoryLoading || isPatientCareOrderHistoryLoading || isPatientOpenWorkflowsLoading) &&
                        <div><LoadingIndicator /></div>
                    }
                    <ActiveTabProvider initialTab={initialTab}>
                        {(!isPatientAppointmentHistoryLoading && !isPatientCareOrderHistoryLoading && !isPatientOpenWorkflowsLoading && !hidePatientHistorySummary()) &&
                            <PatientHistorySummary />
                        }
                        {(!isPatientAppointmentHistoryLoading && !isPatientCareOrderHistoryLoading && !isPatientOpenWorkflowsLoading) &&
                            <>
                                <Nav tabs>
                                    <UpcomingAppointmentNavLink />
                                    <AppointmentHistoryNavLink />
                                    <CareOrderHistoryNavLink
                                        openCareOrdersCount={openCareOrdersCount}
                                    />
                                    <DecisionSupportWorkflowHistoryNavLink />
                                </Nav>
                                <TabContent>
                                    <PatientUpcomingAppointmentsPane />
                                    <PatientAppointmentHistoryPane />
                                    <PatientCareOrderHistoryPane setIsPatientCareOrderHistoryLoading={setIsPatientCareOrderHistoryLoading} />
                                    <PatientOpenWorkflowsPane />
                                </TabContent>
                            </>
                        }
                    </ActiveTabProvider>
                </>
            }
        </div>
    )
}

// Common Base Component for all Nav Links
interface HistoryNavLinkBaseProps {
    label: string;         // The text to display on the nav link
    tabName: PatientInfoTabName;
    children?: React.ReactNode;  // Use children instead of badgeCount/badgeId
    enableCloseActiveTab?: boolean; // Optional prop to enable/disable close tab functionality
}

export const HistoryNavLinkBase: React.FC<HistoryNavLinkBaseProps> = ({
    label,
    tabName,
    children,  // Replace badgeCount and badgeId with children
    enableCloseActiveTab = false,
}) => {
    const { activeTab, setActiveTab } = useActiveTab();
    const isActive = activeTab.name === tabName;
    return (
        <NavItem>
            <NavLink
                className={classnames('patientInfoNavLink', { active: isActive })}
                onClick={() => {
                    if (!isActive) {
                        setActiveTab({ name: tabName });  // reloadTabContent will default to false
                    } else if (enableCloseActiveTab) {
                        setActiveTab(PatientInfoTabName.empty);
                    }
                }}
            >
                {label}
                {children}
            </NavLink>
        </NavItem>
    );
};

// Upcoming Appointment NavLink Component
interface UpcomingAppointmentNavLinkProps { }

const UpcomingAppointmentNavLinkComponent: React.FC<UpcomingAppointmentNavLinkProps> = () => {
    return (
        <HistoryNavLinkBase
            label="Upcoming Appointments"
            tabName={PatientInfoTabName.upcomingAppointment}
        />
    );
};

const UpcomingAppointmentNavLink = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isPatientHistoryEnabled)
)(UpcomingAppointmentNavLinkComponent) as React.FC<UpcomingAppointmentNavLinkProps>;

// Appointment History NavLink Component
interface AppointmentHistoryNavLinkProps { }

const AppointmentHistoryNavLinkComponent: React.FC<AppointmentHistoryNavLinkProps> = () => {
    return (
        <HistoryNavLinkBase
            label="Appointment History"
            tabName={PatientInfoTabName.appointmentHistory}
        />
    );
};

const AppointmentHistoryNavLink = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isPatientHistoryEnabled)
)(AppointmentHistoryNavLinkComponent) as React.FC<AppointmentHistoryNavLinkProps>;


// Care Order History NavLink Component
interface CareOrderHistoryNavLinkProps {
    openCareOrdersCount?: number;
}

const CareOrderHistoryNavLinkComponent: React.FC<CareOrderHistoryNavLinkProps> = ({
    openCareOrdersCount = 0
}) => {
    return (
        <HistoryNavLinkBase
            label="Open Care Orders"
            tabName={PatientInfoTabName.careOrderHistory}
        >
            {/* Render badge as children when count > 0 */}
            {openCareOrdersCount > 0 && (
                <>
                    {' '}
                    <Badge id="openCareOrdersBadge" pill>
                        {openCareOrdersCount}
                    </Badge>
                </>
            )}
        </HistoryNavLinkBase>
    );
};

const CareOrderHistoryNavLink = compose(
    withPermission(Mhd2Permissions.orderView),
    withConfigEnabler((state) => state.config.patient.isPatientCareOrderHistoryEnabled)
)(CareOrderHistoryNavLinkComponent) as React.FC<CareOrderHistoryNavLinkProps>;



// Decision Support Workflow History NavLink Component
interface DecisionSupportWorkflowHistoryNavLinkProps { }

const DecisionSupportWorkflowHistoryNavLinkComponent: React.FC<DecisionSupportWorkflowHistoryNavLinkProps> = ({ }) => {
    return (
        <HistoryNavLinkBase
            label="Open Workflows"
            tabName={PatientInfoTabName.decisionSupportWorkflowHistory}
        />
    );
};

const DecisionSupportWorkflowHistoryNavLink = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isDecisionSupportWorkflowHistoryEnabled)
)(DecisionSupportWorkflowHistoryNavLinkComponent) as React.FC<DecisionSupportWorkflowHistoryNavLinkProps>;




// Base TabPane component that handles common structure
interface HistoryTabPaneBaseProps {
    tabId: PatientInfoTabName;
    children: React.ReactNode;
}

const HistoryTabPaneBase: React.FC<HistoryTabPaneBaseProps> = ({
    tabId,
    children
}) => {
    const { activeTab } = useActiveTab();
    const isActive = activeTab.name === tabId;

    return (
        <TabPane tabId={tabId} className="patientDetailsPageTabPane">
            {isActive && children}
        </TabPane>
    );
};

// Upcoming Appointments Tab Pane
interface PatientUpcomingAppointmentsPaneProps { }

const PatientUpcomingAppointmentsPaneComponent: React.FC<PatientUpcomingAppointmentsPaneProps> = () => {
    return (
        <HistoryTabPaneBase tabId={PatientInfoTabName.upcomingAppointment} >
            <PatientUpcomingAppointments
                showLoseProgressWarning={false}
                enableShowPSAandMHDfilters={true}
            />
        </HistoryTabPaneBase>
    );
};

const PatientUpcomingAppointmentsPane = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isPatientHistoryEnabled)
)(PatientUpcomingAppointmentsPaneComponent) as React.FC<PatientUpcomingAppointmentsPaneProps>;



// Appointment History Tab Pane
interface PatientAppointmentHistoryPaneProps { }

const PatientAppointmentHistoryPaneComponent: React.FC<PatientAppointmentHistoryPaneProps> = () => {

    return (
        <HistoryTabPaneBase tabId={PatientInfoTabName.appointmentHistory}>
            <PatientAppointmentHistory
                showLoseProgressWarning={false}
                enableShowPSAandMHDfilters={true}
            />
        </HistoryTabPaneBase>
    );
};

const PatientAppointmentHistoryPane = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isPatientHistoryEnabled)
)(PatientAppointmentHistoryPaneComponent) as React.FC<PatientAppointmentHistoryPaneProps>;

// Care Order History Tab Pane
interface PatientCareOrderHistoryPaneProps {
    setIsPatientCareOrderHistoryLoading: (isLoading: boolean) => void;
}

const PatientCareOrderHistoryPaneComponent: React.FC<PatientCareOrderHistoryPaneProps> = ({
    setIsPatientCareOrderHistoryLoading
}) => {
    return (
        <HistoryTabPaneBase tabId={PatientInfoTabName.careOrderHistory}>
            <PatientCareOrderHistory
                showBookButton={true}
                showCloseButton={true}
                showFilters={true}
                setIsPatientCareOrderHistoryLoading={setIsPatientCareOrderHistoryLoading}
            />
        </HistoryTabPaneBase>
    );
};

const PatientCareOrderHistoryPane = compose(
    withPermission(Mhd2Permissions.orderView),
    withConfigEnabler((state) => state.config.patient.isPatientCareOrderHistoryEnabled)
)(PatientCareOrderHistoryPaneComponent) as React.FC<PatientCareOrderHistoryPaneProps>;



// Decision Support Workflow History Tab Pane
interface PatientOpenWorkflowsPaneProps { }

const PatientOpenWorkflowsPaneComponent: React.FC<PatientOpenWorkflowsPaneProps> = ({ }) => {
    return (
        <HistoryTabPaneBase tabId={PatientInfoTabName.decisionSupportWorkflowHistory}>
            <PatientOpenWorkflows
                showBookButton={true}
                showLoseProgressWarning={false}
            />
        </HistoryTabPaneBase>
    );
};

const PatientOpenWorkflowsPane = compose(
    withPermission(Mhd2Permissions.appointmentView),
    withConfigEnabler((state) => state.config.patient.isDecisionSupportWorkflowHistoryEnabled)
)(PatientOpenWorkflowsPaneComponent) as React.FC<PatientOpenWorkflowsPaneProps>;



// Omit the activeTab prop from the original props since we'll get it from context
type TabContentPropsWithoutActiveTab = Omit<React.ComponentProps<typeof ReactstrapTabContent>, 'activeTab'>;

/**
 * A wrapper around reactstrap's TabContent that uses the ActiveTab context
 * instead of requiring activeTab to be passed as a prop
 */
export const TabContent: React.FC<TabContentPropsWithoutActiveTab> = (props) => {
    // Get the activeTab from context
    const { activeTab } = useActiveTab();

    // Pass the activeTab from context along with all other props to the original TabContent
    return <ReactstrapTabContent activeTab={activeTab.name} {...props} />;
};